// PdfBausteineSection.tsx — Einstellungen → Unterschriften & Stempel (User 28.09.2026:
// „Signaturen hinterlegen … auch Text zu Signatur/Unterschrift handschriftlich“ und
// „Stempel bei Einstellungen hinterlegen“).
//
// - Unterschriften gehören dem Nutzer: zeichnen, Namen in Handschrift tippen oder Bild
//   hochladen; optional Text darunter (z. B. Name · Funktion) und Datum.
// - Stempel gehören dem Büro: alle sehen sie, pflegen darf nur die Leitung (admin/gfgl) —
//   dieselbe Regel prüft der Server (`apps/api/src/pdf-bausteine/pdf-baustein.regeln.ts`).
// - Löschen fragt in der Karte nach (kein `window.confirm`), gelöscht wird weich.
// Eingesetzt werden die Bausteine im PDF-Editor (`components/documents/pdf-editor/`).

import * as React from "react";
import {
    Button,
    Checkbox,
    Field,
    Input,
    MessageBar,
    MessageBarBody,
    Radio,
    RadioGroup,
    Spinner,
    Textarea,
    makeStyles,
    shorthands,
} from "@fluentui/react-components";
import { REFINED } from "../../components/risikoanalyseStep/tokens";
import { SectionCard } from "../../components/forms/formPrimitives";
import { useAuth } from "../../context/AuthContext";
import {
    pdfBausteinService,
    type BausteinArt,
    type BausteinEingabe,
    type PdfBaustein,
} from "../../services/pdfBausteinService";
import { bausteinAlsPng } from "../../components/documents/pdf-editor/bausteinBild";
import { UnterschriftZeichnen } from "./UnterschriftZeichnen";
import { textAlsHandschriftPng } from "./handschrift";
import { bildDateiVorbereiten, blobAlsDataUrl } from "./bildVorbereiten";

const STANDARD_FARBE = "#1f3a93";

const FARBEN_UNTERSCHRIFT = [
    { wert: "#1f3a93", label: "Blau" },
    { wert: "#000000", label: "Schwarz" },
];

const FARBEN_STEMPEL = [
    { wert: "#1f3a93", label: "Blau" },
    { wert: "#c0392b", label: "Rot" },
    { wert: "#1e8449", label: "Grün" },
    { wert: "#000000", label: "Schwarz" },
];

/** Woher das Bild des Bausteins kommt. `behalten` = beim Bearbeiten das gespeicherte Bild. */
type Quelle = "zeichnen" | "tippen" | "bild" | "text" | "behalten";

export interface Entwurf {
    id: number | null;
    art: BausteinArt;
    name: string;
    text: string;
    farbe: string;
    mitDatum: boolean;
    handschrift: boolean;
    quelle: Quelle;
    /** Gespeichertes Bild (nur beim Bearbeiten). */
    bisher: string | null;
    gezeichnet: string | null;
    getippt: string;
    getipptBild: string | null;
    hochgeladen: string | null;
}

const neuerEntwurf = (art: BausteinArt): Entwurf => ({
    id: null,
    art,
    name: "",
    text: "",
    farbe: STANDARD_FARBE,
    mitDatum: false,
    handschrift: false,
    quelle: art === "unterschrift" ? "zeichnen" : "text",
    bisher: null,
    gezeichnet: null,
    getippt: "",
    getipptBild: null,
    hochgeladen: null,
});

const entwurfAus = (b: PdfBaustein): Entwurf => ({
    ...neuerEntwurf(b.art),
    id: b.id,
    name: b.name,
    text: b.text,
    farbe: b.farbe,
    mitDatum: b.mitDatum,
    handschrift: b.handschrift,
    quelle: b.bildDataUrl ? "behalten" : "text",
    bisher: b.bildDataUrl,
});

export function bildVon(e: Entwurf): string | null {
    switch (e.quelle) {
        case "behalten":
            return e.bisher;
        case "zeichnen":
            return e.gezeichnet;
        case "tippen":
            return e.getipptBild;
        case "bild":
            return e.hochgeladen;
        default:
            return null;
    }
}

/** Bild-Stempel tragen keinen Text — das Feld ist dort ausgeblendet und wird nicht gezeichnet. */
const mitText = (e: Entwurf) => e.art === "unterschrift" || e.quelle === "text";

export function eingabeVon(e: Entwurf): BausteinEingabe {
    return {
        art: e.art,
        name: e.name.trim(),
        bildDataUrl: bildVon(e),
        text: mitText(e) ? e.text.trim() : "",
        farbe: e.farbe,
        mitDatum: e.mitDatum,
        handschrift: e.art === "unterschrift" && e.handschrift,
    };
}

function fehlt(e: Entwurf): string | null {
    const eingabe = eingabeVon(e);
    if (!eingabe.name) return "Bitte einen Namen vergeben.";
    if (e.art === "unterschrift" && !eingabe.bildDataUrl) {
        return e.quelle === "tippen" ? "Bitte den Namen eintippen." : e.quelle === "bild" ? "Bitte ein Bild wählen." : "Bitte unterschreiben.";
    }
    if (e.art === "stempel" && !eingabe.bildDataUrl && !eingabe.text) {
        return e.quelle === "bild" ? "Bitte ein Bild wählen." : "Bitte den Stempeltext eintragen.";
    }
    return null;
}

const useStyles = makeStyles({
    wurzel: { display: "flex", flexDirection: "column", rowGap: "16px", fontFamily: REFINED.fontUI },
    intro: { color: REFINED.ink2, fontSize: "0.875rem", lineHeight: 1.5, ...shorthands.margin(0) },
    raster: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", columnGap: "12px", rowGap: "12px" },
    karte: {
        display: "flex",
        flexDirection: "column",
        rowGap: "8px",
        ...shorthands.padding("12px"),
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("10px"),
        backgroundColor: REFINED.white,
    },
    vorschauBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "90px",
        backgroundColor: REFINED.fieldBg,
        ...shorthands.borderRadius("8px"),
        ...shorthands.padding("8px"),
    },
    vorschauBild: { maxWidth: "100%", maxHeight: "120px", objectFit: "contain" },
    name: { fontSize: "0.9rem", fontWeight: 600, color: REFINED.ink, overflowWrap: "anywhere" },
    aktionen: { display: "flex", flexWrap: "wrap", columnGap: "6px", rowGap: "6px", alignItems: "center" },
    frage: { fontSize: "0.8rem", color: REFINED.err, fontWeight: 600 },
    leer: { color: REFINED.ink3, fontSize: "0.85rem" },
    hinweis: { color: REFINED.ink3, fontSize: "0.8rem" },
    formular: {
        display: "flex",
        flexDirection: "column",
        rowGap: "12px",
        ...shorthands.padding("16px"),
        ...shorthands.border("1px", "dashed", REFINED.aub200),
        ...shorthands.borderRadius("10px"),
        backgroundColor: REFINED.aub50,
    },
    zweiSpalten: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        columnGap: "16px",
        rowGap: "12px",
        "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    spalte: { display: "flex", flexDirection: "column", rowGap: "10px", minWidth: 0 },
});

/** Zeigt das fertige Bild genau so, wie es später im PDF landet. */
const Vorschau: React.FC<{ baustein: PdfBaustein }> = ({ baustein }) => {
    const s = useStyles();
    const [url, setUrl] = React.useState<string | null>(null);
    const [fehler, setFehler] = React.useState(false);
    const schluessel = JSON.stringify([baustein.art, baustein.bildDataUrl?.length, baustein.bildDataUrl?.slice(-64), baustein.text, baustein.farbe, baustein.mitDatum, baustein.handschrift]);
    React.useEffect(() => {
        let aktiv = true;
        let erzeugt: string | null = null;
        setFehler(false);
        bausteinAlsPng(baustein)
            .then((blob) => {
                if (!aktiv) return;
                erzeugt = URL.createObjectURL(blob);
                setUrl(erzeugt);
            })
            .catch(() => aktiv && setFehler(true));
        return () => {
            aktiv = false;
            if (erzeugt) URL.revokeObjectURL(erzeugt);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schluessel]);
    return (
        <div className={s.vorschauBox}>
            {fehler ? (
                <span className={s.hinweis}>Vorschau nicht möglich</span>
            ) : url ? (
                <img className={s.vorschauBild} src={url} alt={`Vorschau: ${baustein.name || "Baustein"}`} />
            ) : (
                <Spinner size="tiny" />
            )}
        </div>
    );
};

const Karte: React.FC<{
    b: PdfBaustein;
    busy: boolean;
    onBearbeiten: () => void;
    onLoeschen: () => Promise<void>;
}> = ({ b, busy, onBearbeiten, onLoeschen }) => {
    const s = useStyles();
    const [frage, setFrage] = React.useState(false);
    return (
        <div className={s.karte} data-baustein={b.id}>
            <Vorschau baustein={b} />
            <span className={s.name}>{b.name}</span>
            {b.eigen && (
                <div className={s.aktionen}>
                    {frage ? (
                        <>
                            <span className={s.frage}>Wirklich löschen?</span>
                            <Button size="small" appearance="primary" disabled={busy} onClick={() => void onLoeschen()}>
                                Ja, löschen
                            </Button>
                            <Button size="small" disabled={busy} onClick={() => setFrage(false)}>
                                Nein
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button size="small" disabled={busy} onClick={onBearbeiten}>
                                Bearbeiten
                            </Button>
                            <Button size="small" disabled={busy} onClick={() => setFrage(true)}>
                                Löschen
                            </Button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const Formular: React.FC<{
    start: Entwurf;
    busy: boolean;
    onSpeichern: (e: Entwurf) => Promise<void>;
    onAbbrechen: () => void;
}> = ({ start, busy, onSpeichern, onAbbrechen }) => {
    const s = useStyles();
    const [e, setE] = React.useState<Entwurf>(start);
    const [bildFehler, setBildFehler] = React.useState<string | null>(null);
    const setze = React.useCallback((teil: Partial<Entwurf>) => setE((alt) => ({ ...alt, ...teil })), []);
    const unterschrift = e.art === "unterschrift";
    const titel = unterschrift ? "Unterschrift bearbeiten" : "Stempel bearbeiten";

    // Getippter Name → Handschrift-Bild. Kurz warten, damit nicht jeder Tastendruck malt.
    React.useEffect(() => {
        if (e.quelle !== "tippen") return;
        if (!e.getippt.trim()) {
            setze({ getipptBild: null });
            return;
        }
        let aktiv = true;
        const t = window.setTimeout(() => {
            textAlsHandschriftPng(e.getippt, e.farbe)
                .then(blobAlsDataUrl)
                .then((url) => aktiv && setze({ getipptBild: url }))
                .catch(() => aktiv && setze({ getipptBild: null }));
        }, 200);
        return () => {
            aktiv = false;
            window.clearTimeout(t);
        };
    }, [e.quelle, e.getippt, e.farbe, setze]);

    const onGezeichnet = React.useCallback((url: string | null) => setze({ gezeichnet: url }), [setze]);

    const hochladen = async (datei: File | undefined) => {
        if (!datei) return;
        setBildFehler(null);
        try {
            setze({ hochgeladen: await bildDateiVorbereiten(datei) });
        } catch (err) {
            setBildFehler(err instanceof Error ? err.message : "Das Bild ließ sich nicht verwenden.");
        }
    };

    const problem = fehlt(e);
    const vorschau: PdfBaustein = { ...eingabeVon(e), id: e.id ?? 0, sortierung: 0, eigen: true, geaendert: null };
    const farben = unterschrift ? FARBEN_UNTERSCHRIFT : FARBEN_STEMPEL;

    const quellen: { wert: Quelle; label: string }[] = unterschrift
        ? [
              ...(e.bisher ? [{ wert: "behalten" as Quelle, label: "Bisherige behalten" }] : []),
              { wert: "zeichnen", label: "Zeichnen" },
              { wert: "tippen", label: "Tippen" },
              { wert: "bild", label: "Bild hochladen" },
          ]
        : [
              ...(e.bisher ? [{ wert: "behalten" as Quelle, label: "Bisheriges Bild" }] : []),
              { wert: "bild", label: "Bild-Stempel" },
              { wert: "text", label: "Text-Stempel" },
          ];

    return (
        <form
            className={s.formular}
            aria-label={titel}
            onSubmit={(ev) => {
                ev.preventDefault();
                if (!problem) void onSpeichern(e);
            }}
        >
            <RadioGroup
                layout="horizontal"
                aria-label={unterschrift ? "Wie soll die Unterschrift entstehen?" : "Art des Stempels"}
                value={e.quelle}
                onChange={(_, d) => setze({ quelle: d.value as Quelle })}
            >
                {quellen.map((q) => (
                    <Radio key={q.wert} value={q.wert} label={q.label} />
                ))}
            </RadioGroup>

            <div className={s.zweiSpalten}>
                <div className={s.spalte}>
                    {e.quelle === "zeichnen" && <UnterschriftZeichnen farbe={e.farbe} onAenderung={onGezeichnet} vorhanden={e.gezeichnet} />}
                    {e.quelle === "tippen" && (
                        <Field label="Name in Handschrift" hint="Wird in einer Handschrift-Schrift gesetzt.">
                            <Input value={e.getippt} onChange={(_, d) => setze({ getippt: d.value })} placeholder="z. B. M. El Jemai" />
                        </Field>
                    )}
                    {e.quelle === "bild" && (
                        <Field
                            label="Bild (PNG oder JPG)"
                            hint="Weißer Hintergrund wird automatisch durchsichtig."
                            validationMessage={bildFehler ?? undefined}
                        >
                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={(ev) => void hochladen(ev.currentTarget.files?.[0])}
                            />
                        </Field>
                    )}

                    <Field label="Name" required hint={unterschrift ? "Nur für Sie sichtbar, z. B. „Vollständig“ oder „Kürzel“." : "Erscheint im Stempel-Menü des PDF-Editors."}>
                        <Input value={e.name} onChange={(_, d) => setze({ name: d.value })} />
                    </Field>
                    {mitText(e) && (
                        <Field label={unterschrift ? "Text unter der Unterschrift" : "Stempeltext (eine Zeile je Zeile)"}>
                            <Textarea
                                value={e.text}
                                onChange={(_, d) => setze({ text: d.value })}
                                placeholder={unterschrift ? "z. B. Mohamed El Jemai · Geschäftsführer" : "z. B. EINGEGANGEN"}
                                resize="vertical"
                            />
                        </Field>
                    )}
                    {unterschrift && (
                        <Checkbox
                            label="Text in Handschrift"
                            checked={e.handschrift}
                            onChange={(_, d) => setze({ handschrift: !!d.checked })}
                        />
                    )}
                    <Checkbox label="Datum anfügen" checked={e.mitDatum} onChange={(_, d) => setze({ mitDatum: !!d.checked })} />
                    <RadioGroup
                        layout="horizontal"
                        aria-label="Farbe"
                        value={e.farbe}
                        onChange={(_, d) => setze({ farbe: d.value })}
                    >
                        {farben.map((f) => (
                            <Radio key={f.wert} value={f.wert} label={f.label} />
                        ))}
                    </RadioGroup>
                    {e.quelle === "zeichnen" && (
                        <span className={s.hinweis}>Die Farbe gilt für neue Striche.</span>
                    )}
                </div>
                <div className={s.spalte}>
                    <span className={s.hinweis}>So sieht es im PDF aus (Datum = Tag des Einsetzens):</span>
                    {bildVon(e) || (!unterschrift && mitText(e) && e.text.trim()) ? (
                        <Vorschau baustein={vorschau} />
                    ) : (
                        <div className={s.vorschauBox}>
                            <span className={s.hinweis}>{problem}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className={s.aktionen}>
                <Button type="submit" appearance="primary" disabled={busy || !!problem} title={problem ?? undefined}>
                    Speichern
                </Button>
                <Button type="button" disabled={busy} onClick={onAbbrechen}>
                    Abbrechen
                </Button>
                {problem && <span className={s.hinweis}>{problem}</span>}
            </div>
        </form>
    );
};

export const PdfBausteineSection: React.FC = () => {
    const s = useStyles();
    const { isAdmin, isGfgl } = useAuth();
    const leitung = isAdmin || isGfgl;
    const [liste, setListe] = React.useState<PdfBaustein[] | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [entwurf, setEntwurf] = React.useState<Entwurf | null>(null);

    const laden = React.useCallback(async () => {
        try {
            setListe(await pdfBausteinService.liste());
        } catch (err) {
            setFehler(err instanceof Error ? err.message : "Laden fehlgeschlagen.");
            setListe([]);
        }
    }, []);

    React.useEffect(() => {
        void laden();
    }, [laden]);

    const speichern = async (e: Entwurf) => {
        setBusy(true);
        setFehler(null);
        try {
            const eingabe = eingabeVon(e);
            if (e.id == null) await pdfBausteinService.anlegen(eingabe);
            else await pdfBausteinService.aendern(e.id, eingabe);
            setEntwurf(null);
            await laden();
        } catch (err) {
            setFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    const loeschen = async (id: number) => {
        setBusy(true);
        setFehler(null);
        try {
            await pdfBausteinService.loeschen(id);
            await laden();
        } catch (err) {
            setFehler(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    const abschnitt = (art: BausteinArt) => {
        const eintraege = (liste ?? []).filter((b) => b.art === art);
        const darfAnlegen = art === "unterschrift" || leitung;
        const offen = entwurf?.art === art;
        return (
            <SectionCard
                title={art === "unterschrift" ? "Meine Unterschriften" : "Stempel des Büros"}
                actions={
                    darfAnlegen && !offen ? (
                        <Button appearance="primary" disabled={busy || entwurf != null} onClick={() => setEntwurf(neuerEntwurf(art))}>
                            {art === "unterschrift" ? "+ Unterschrift anlegen" : "+ Stempel anlegen"}
                        </Button>
                    ) : undefined
                }
            >
                {art === "stempel" && !leitung && <span className={s.hinweis}>Stempel pflegt die Leitung.</span>}
                {offen && entwurf && (
                    <Formular
                        key={entwurf.id ?? "neu"}
                        start={entwurf}
                        busy={busy}
                        onSpeichern={speichern}
                        onAbbrechen={() => setEntwurf(null)}
                    />
                )}
                {liste == null ? (
                    <Spinner size="small" label="Wird geladen…" />
                ) : eintraege.length === 0 ? (
                    <span className={s.leer}>
                        {art === "unterschrift" ? "Noch keine Unterschrift hinterlegt." : "Noch keine Stempel hinterlegt."}
                    </span>
                ) : (
                    <div className={s.raster}>
                        {eintraege.map((b) => (
                            <Karte
                                key={b.id}
                                b={b}
                                busy={busy || entwurf != null}
                                onBearbeiten={() => setEntwurf(entwurfAus(b))}
                                onLoeschen={() => loeschen(b.id)}
                            />
                        ))}
                    </div>
                )}
            </SectionCard>
        );
    };

    return (
        <div className={s.wurzel}>
            <p className={s.intro}>
                Hier hinterlegen Sie Ihre Unterschriften. Im PDF-Editor setzen Sie sie dann mit „Unterschrift ▾“ per
                Klick an die richtige Stelle — ebenso die Stempel des Büros mit „Stempel ▾“.
            </p>
            {fehler && (
                <MessageBar intent="error">
                    <MessageBarBody>{fehler}</MessageBarBody>
                </MessageBar>
            )}
            {abschnitt("unterschrift")}
            {abschnitt("stempel")}
        </div>
    );
};

export default PdfBausteineSection;
